import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AgCharts } from "ag-charts-react";

import { salaryAbsencesQuery } from "../../api";
import { customChartThemes } from "../../common/grid";
import { LoadingContainer } from "../../common/loaders/loadingcontainer";
import { localized } from "../../common/utils";

export function SicknessAbsenceHours() {
  const { t } = useTranslation();

  const { data } = useQuery(salaryAbsencesQuery);

  const [combinedData, setCombinedData] = useState();

  const { chartTheme } = customChartThemes;

  useEffect(() => {
    if (data) {
      const salaryabsences = data.salaryabsences_response.salaryabsences ?? [];

      const last12MonthsData = salaryabsences.filter(item => isDateInLast12Months(item.STARTDATE));

        //PAYTYPE values 1, 101, 102, 2, 20
      const sicknessLeavesData = last12MonthsData.filter(item =>
        ["1", "101", "102", "2", "20"].includes(item.ABSENCETYPE)
      );

      const filteredData = sicknessLeavesData.filter(person => person.HOURS > 0);

      const paidAbsenceData = filteredData.filter(item => item.PAID === 1 || !item.PAID);
      const nonPaidAbsenceData = filteredData.filter(item => item.PAID === 0);

      const totalPaidAbsencesPerPerson = calculateTotalAbsencesPerPerson(paidAbsenceData);
      const totalNonPaidAbsencesPerPerson = calculateTotalAbsencesPerPerson(nonPaidAbsenceData);

      setCombinedData(totalPaidAbsencesPerPerson.map(paidItem => {
        const nonPaidItem = totalNonPaidAbsencesPerPerson.find(
          nonPaidItem => nonPaidItem.personName === paidItem.personName
        );

        return {
          personName: paidItem.personName,
          totalPaidHours: paidItem.totalHours,
          totalNonPaidHours: nonPaidItem ? nonPaidItem.totalHours : 0,
        };
      }));
    }
  }, [data]);

  function calculateTotalAbsencesPerPerson(data) {
    const totalAbsences = {};

    data.forEach(item => {
      const hours = item.HOURS;
      const personName = item.PERSONNAME;

      totalAbsences[personName] = (totalAbsences[personName] || 0) + hours;
    });

    const formattedData = Object.keys(totalAbsences).map(personName => ({
      personName: personName,
      totalHours: totalAbsences[personName],
    }));
    return formattedData;
  }

  function isDateInLast12Months(dateString) {
    const currentDate = new Date();
    const date = new Date(dateString.replace(/(\d{2}).(\d{2}).(\d{4})/, "$3-$2-$1"));
    const last12Months = new Date(currentDate.setMonth(currentDate.getMonth() - 12));

    return date >= last12Months;
  }

  /** @type {import("ag-grid-enterprise").AgChartOptions} */
  const options = {
    data: combinedData,
    series: [
      {
        type: "bar",
        direction: "horizontal",
        xKey: "personName",
        yKey: "totalPaidHours",
        yName: t("WithPay", "Palkallinen"),
      },
      {
        type: "bar",
        direction: "horizontal",
        xKey: "personName",
        yKey: "totalNonPaidHours",
        yName: t("WithoutPay", "Palkaton"),
      },
    ],
    theme: { ...chartTheme, palette: { ...chartTheme.palette, fills: ["#B7A0E0", "#FFC107"] } },
    legend: {
      enabled: true,
    },
    overlays: {
      noData: { text: `${t("No", "Ei")} ${t("data", "tietoja")}` },
    },
    locale: localized.agChartsLocale,
  };


  return combinedData ? (
    <>
      <h4 style={{ color: "var(--heeros-blue)" }}>{t("SicknessAbsenceHours", "Sairauspoissaolotunnit")}</h4>

      <div>
        <AgCharts options={options} />
      </div>
    </>
  ) : (
    <LoadingContainer />
  );
}
